import { FC, useCallback, useMemo } from 'react';

import { PlateformeDto } from 'api/plateforme';
import { usePanel } from 'hooks/usePanel';
import { Sizes } from 'enums/sizes';

import { ThemeEditForm } from './form/ThemeEditForm';

interface Props {
  plateforme: PlateformeDto;
  refresh: () => void;
}

export const ThemeMosaicHeaderBadge: FC<Props> = ({ plateforme, refresh }) => {
  const { openPanel } = usePanel();
  const { theme } = plateforme;

  const open = useCallback(() => {
    openPanel(
      { title: 'Modifier le thème', size: Sizes['4xl'] },
      <ThemeEditForm plateforme={plateforme} refresh={refresh} />,
    );
  }, [openPanel, plateforme, refresh]);

  const swatches = useMemo(() => {
    if (!theme) return [];
    return [
      { c: theme.primaryColor, l: 'Primaire' },
      { c: theme.secondaryColor, l: 'Secondaire' },
      { c: theme.accentColor, l: 'Accent' },
      { c: theme.successColor, l: 'Succès' },
      { c: theme.errorColor, l: 'Erreur' },
      { c: theme.warningColor, l: 'Warning' },
      { c: theme.infoColor, l: 'Info' },
      { c: theme.backgroundColor, l: 'Fond' },
      { c: theme.surfaceColor, l: 'Surface' },
    ];
  }, [theme]);

  return (
    <button
      type="button"
      onClick={open}
      title={theme ? 'Modifier le thème' : 'Configurer le thème'}
      className="group flex flex-col gap-2 rounded-xl border border-gray-200 bg-white hover:border-primary/40 hover:shadow-md transition-all px-4 py-3 min-w-[260px]"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider leading-none">
          Thème
        </span>
        {theme?.fontFamily ? (
          <span
            className="text-sm text-gray-800 truncate"
            style={{ fontFamily: theme.fontFamily }}
            title={`Typographie : ${theme.fontFamily}`}
          >
            {theme.fontFamily}
          </span>
        ) : (
          <span className="text-xs text-gray-400 italic">Sans typographie</span>
        )}
      </div>

      {theme ? (
        <div className="rounded-md overflow-hidden ring-1 ring-black/10 shadow-sm">
          <div className="flex">
            {swatches.map((s) => (
              <div
                key={s.l}
                title={`${s.l} · ${s.c}`}
                className="h-6 flex-1 transition-transform"
                style={{ backgroundColor: s.c }}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-md overflow-hidden ring-1 ring-black/10 shadow-sm">
          <div className="flex">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="h-6 flex-1 bg-gray-100" />
            ))}
          </div>
        </div>
      )}
    </button>
  );
};
